import React from "react";
import { Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { C, DISPLAY_FONT } from "../../common/theme";

interface KpiCardProps {
  title: string;
  value: string;
  /** Secondary line under the value */
  sub?: React.ReactNode;
  /** Relative change vs the comparison period; null hides it */
  delta?: number | null;
  deltaLabel?: string;
  /** Lower is better (e.g. refunds) */
  invertDelta?: boolean;
  help?: string;
  onClick?: () => void;
  active?: boolean;
  tone?: "default" | "warning";
}

function Delta({ delta, label, invert }: { delta: number; label?: string; invert?: boolean }) {
  const up = delta >= 0;
  const good = invert ? !up : up;
  const Icon = up ? NorthEastIcon : SouthEastIcon;
  return (
    <Typography component="span" variant="caption" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color: good ? C.leaf : C.danger, fontWeight: 700 }}>
      <Icon sx={{ fontSize: 13 }} />
      {Math.abs(delta * 100).toFixed(Math.abs(delta) < 0.1 ? 1 : 0)}%
      {label && (
        <Box component="span" sx={{ color: C.muted, fontWeight: 500, ml: 0.5 }}>
          {label}
        </Box>
      )}
    </Typography>
  );
}

/** A number tile: small label, big Bricolage value, delta or detail line.
 * Clickable tiles act as filters (active = selected). */
export default function KpiCard({ title, value, sub, delta, deltaLabel, invertDelta, help, onClick, active, tone = "default" }: KpiCardProps) {
  const body = (
    <Box sx={{ p: 1.75, width: "100%", boxSizing: "border-box", textAlign: "left" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="caption" sx={{ color: C.muted, fontWeight: 600 }} noWrap>
          {title}
        </Typography>
        {help && (
          <Tooltip title={help}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: C.faint }} />
          </Tooltip>
        )}
      </Box>
      <Typography
        sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: 24, letterSpacing: "-0.035em", lineHeight: 1.15, mt: 0.5, color: tone === "warning" ? C.coralText : C.ink, overflowWrap: "anywhere" }}
      >
        {value}
      </Typography>
      <Box sx={{ minHeight: 20, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.25 }}>
        {delta !== undefined && delta !== null && <Delta delta={delta} label={deltaLabel} invert={invertDelta} />}
        {sub && (
          <Typography variant="caption" sx={{ color: C.muted }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  );
  const frameSx = {
    height: "100%",
    width: "100%",
    display: "flex",
    bgcolor: tone === "warning" ? C.bone1 : C.paper,
    border: `${active ? 2 : 1}px solid ${active ? C.ink : tone === "warning" ? "#F2C5AF" : C.line}`,
    borderRadius: "8px",
    boxSizing: "border-box" as const,
    m: active ? 0 : "0",
  };
  return onClick ? (
    <ButtonBase onClick={onClick} aria-pressed={active} sx={{ ...frameSx, alignItems: "stretch", "&:hover": { borderColor: C.ink }, "&:focus-visible": { outline: `2px solid ${C.coral}`, outlineOffset: 2 } }}>
      {body}
    </ButtonBase>
  ) : (
    <Box sx={frameSx}>{body}</Box>
  );
}

/** A responsive row of KpiCards: 2 per row on phones, up to `max` across. */
export function KpiStrip({ children, max = 4 }: { children: React.ReactNode; max?: number }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.25,
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          md: `repeat(${Math.min(max, 3)}, minmax(0, 1fr))`,
          lg: `repeat(${max}, minmax(0, 1fr))`,
        },
      }}
    >
      {children}
    </Box>
  );
}
